import type { Field } from './field';
import type { Category, Department, FieldConfig, FieldOverride } from './issue';

const toFieldOverride = (config: FieldConfig): FieldOverride => ({
  ...(config.label ? { label: config.label } : {}),
  ...(config.placeholder ? { placeholder: config.placeholder } : {}),
  ...(config.options?.length
    ? {
        options: config.options.map((option) => ({
          id: option,
          label: option,
        })),
      }
    : {}),
  ...(config.isRequired !== undefined ? { isRequired: config.isRequired } : {}),
});

const indexOverridesByFieldKey = (
  configs: FieldConfig[]
): Record<string, FieldOverride> =>
  Object.fromEntries(
    configs.flatMap((config) => {
      const override = toFieldOverride(config);

      return Object.keys(override).length > 0
        ? [[config.key, override] as const]
        : [];
    })
  );

const overrideField = (field: Field, override: FieldOverride): Field => ({
  ...field,
  ...(override.label !== undefined ? { label: override.label } : {}),
  ...(override.placeholder !== undefined
    ? { placeholder: override.placeholder }
    : {}),
  ...(override.options !== undefined ? { options: override.options } : {}),
  ...(override.isRequired !== undefined
    ? { isRequired: override.isRequired }
    : {}),
});

export const getMergedCatalogFieldsForSelection = (
  base: Record<string, Field>,
  department: Department | undefined,
  category: Category | undefined
): Record<string, Field> => {
  const activeFields = category ? category.fields : department?.fields;
  if (!activeFields) {
    return base;
  }

  const overridesByFieldKey = indexOverridesByFieldKey(activeFields);
  if (Object.keys(overridesByFieldKey).length === 0) {
    return base;
  }

  return Object.fromEntries(
    Object.entries(base).map(([id, field]) => {
      const override = overridesByFieldKey[id];
      return [id, override ? overrideField(field, override) : field];
    })
  );
};
